import React, { useEffect, useRef, useState } from 'react';
import { 
  FaStar, FaStarHalfAlt, FaQuoteLeft, FaQuoteRight,
  FaUserCircle, FaGraduationCap, FaHeart,
  FaChalkboardTeacher, FaBookReader, FaSmile, FaTrophy, FaThumbsUp
} from 'react-icons/fa';

const ReviewSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const reviews = [
    {
      id: 1,
      name: "Priya Sharma",
      course: "B.Com Graduate",
      image: "https://randomuser.me/api/portraits/women/1.jpg",
      rating: 5,
      review: "Excellent learning environment. Supportive faculty and top-notch infrastructure.",
      achievement: "Working at HDFC Bank"
    },
    {
      id: 2,
      name: "Neha Gupta",
      course: "B.Sc Graduate",
      image: "https://randomuser.me/api/portraits/women/2.jpg",
      rating: 5,
      review: "Well-equipped labs and knowledgeable teachers. Practical learning at its best.",
      achievement: "Pursuing M.Sc at BHU"
    },
    {
      id: 3,
      name: "Anjali Singh",
      course: "B.A Graduate",
      image: "https://randomuser.me/api/portraits/women/3.jpg",
      rating: 4.5,
      review: "Great faculty and library. Cultural events helped in personality development.",
      achievement: "Cleared UPSC Prelims"
    },
    {
      id: 4,
      name: "Ritu Verma",
      course: "B.Com Student",
      image: "https://randomuser.me/api/portraits/women/4.jpg",
      rating: 5,
      review: "Excellent teaching methodology. Career guidance and placement assistance provided.",
      achievement: "Placed in TCS"
    },
    {
      id: 5,
      name: "Sneha Patel",
      course: "B.Sc Student",
      image: "https://randomuser.me/api/portraits/women/5.jpg",
      rating: 4.5,
      review: "Beautiful campus with modern facilities. Encourages extracurricular activities.",
      achievement: "State Sports Champion"
    }
  ];

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<FaStar key={i} className="text-yellow-400 text-xs" />);
    }
    if (hasHalfStar) {
      stars.push(<FaStarHalfAlt key="half" className="text-yellow-400 text-xs" />);
    }
    return stars;
  };

  return (
    <section ref={sectionRef} className="w-full bg-gradient-to-br from-yellow-50 via-white to-blue-50 py-16">
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          
          {/* Decorative Top */}
          <div className="flex justify-center gap-3 mb-6">
            <div className="w-12 h-1 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full"></div>
            <div className="w-8 h-1 bg-gradient-to-r from-orange-400 to-orange-500 rounded-full"></div>
            <div className="w-6 h-1 bg-gradient-to-r from-blue-400 to-blue-500 rounded-full"></div>
          </div>

          {/* Heading */}
          <div className="text-center mb-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-3">
              <span className="bg-gradient-to-r from-yellow-500 via-orange-500 to-blue-500 bg-clip-text text-transparent">
                Student Reviews
              </span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 via-orange-500 to-blue-500 rounded-full mx-auto"></div>
            <p className="text-gray-600 text-sm mt-4">
              What our students say about us
            </p>
          </div>

          {/* 5 Cards in One Row */}
          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            {reviews.map((review, idx) => (
              <div key={idx} className="bg-white rounded-xl p-4 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full">
                {/* Student Image and Name */}
                <div className="flex items-center gap-3 mb-3">
                  <img 
                    src={review.image}
                    alt={review.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-800 text-sm">{review.name}</h4>
                    <p className="text-gray-500 text-xs">{review.course}</p>
                  </div>
                </div>
                
                {/* Rating Stars */}
                <div className="flex items-center gap-1 mb-3">
                  {renderStars(review.rating)}
                </div>
                
                {/* Review Text */}
                <p className="text-gray-600 text-xs leading-relaxed flex-grow">
                  "{review.review}"
                </p>
                
                {/* Achievement */}
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-yellow-600 text-xs font-semibold flex items-center gap-1">
                    <FaTrophy className="text-xs" />
                    {review.achievement}
                  </p>
                </div>
              </div>
            ))}
          </div>
          {/* Write Review CTA */}
          <div className={`mt-10 text-center transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <button className="bg-gradient-to-r from-yellow-400 via-orange-500 to-blue-500 text-white px-5 py-2 rounded-lg font-semibold text-sm hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center gap-2 mx-auto">
              <FaBookReader className="text-sm" />
              Share Your Experience
            </button>
          </div>

          {/* Bottom Decorative */}
          <div className="mt-10 flex justify-center gap-2">
            <div className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .animate-bounce {
          animation: bounce 1s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default ReviewSection;